// Shared Launch-readiness math: average of written-plan completion,
// roadmap completion and compliance completion (mirrors the Business Hub).
import { computeComplianceScore } from "../components/ComplianceScoreCard";
import {
  CORE_SECTIONS,
  sectionDone,
  hasHealthCheck,
  hasRoadmap,
  hasCompliance,
} from "./planSections";

// Re-exported from the dependency-free planSections module so existing
// `../lib/launchReadiness` import sites keep working.
export { CORE_SECTIONS, sectionDone, hasHealthCheck, hasRoadmap, hasCompliance };


export function planProgress(business) {
  const plan = business?.plan;
  if (!plan) return 0;
  const done = CORE_SECTIONS.filter((k) => sectionDone(business, k)).length;
  return Math.round((done / CORE_SECTIONS.length) * 100);
}

export function roadmapProgress(business) {
  const items = business?.roadmap || [];
  if (!items.length) return 0;
  const done = items.filter((r) => r.status === "done").length;
  return Math.round((done / items.length) * 100);
}

export function compliancePctOf(business) {
  const c = computeComplianceScore(business?.compliance);
  return c && c.total ? Math.round((c.done / c.total) * 100) : 0;
}

// Feb 2027 · Launch Health's "Plan" leg now surfaces the Plan Health
// score (the deterministic + LLM hybrid from `business.health_check`)
// instead of the section-completion percentage. Founders correctly
// pointed out that "10 sections filled" is a shallow signal — the
// Health Check is the deep verdict, so it deserves to drive the pill
// alongside Roadmap + Compliance. `planProgress` stays exported (still
// used by tour ribbons, the readiness-sort column, etc.). Returns 0
// when the founder hasn't run a health check yet so the pill degrades
// gracefully to the neutral "—" state via the LegPill's `missing` path.
export function planHealthScore(business) {
  const score = business?.health_check?.score;
  if (typeof score !== "number" || Number.isNaN(score)) return 0;
  return Math.max(0, Math.min(100, Math.round(score)));
}

export function launchReadinessPct(business) {
  const hasPlan = !!business?.plan;
  return Math.round(((hasPlan ? planHealthScore(business) : 0) + roadmapProgress(business) + compliancePctOf(business)) / 3);
}

/**
 * The single most launch-ready business in a list — the "furthest plan
 * toward launch". Skips soft-deleted / inactive plans, and returns
 * `null` when the list is empty or every plan is 0%. Shared by the
 * Dashboard Journey Strip (names the plan next to the ring) and the
 * Dashboard tile grid (paints a Rocket badge on the matching tile).
 */
export function furthestBusiness(businesses) {
  const active = (businesses || []).filter(
    (b) => b?.is_active !== false && !b?.deleted_at,
  );
  if (!active.length) return null;
  const winner = active.reduce(
    (best, b) => (launchReadinessPct(b) > launchReadinessPct(best) ? b : best),
    active[0],
  );
  // No point crowning a 0% plan — the ring shows 0 already and the
  // badge on the tile would be misleading.
  return launchReadinessPct(winner) > 0 ? winner : null;
}
